import React, { useState } from 'react';
import { Bookmark, Plus, Trash2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { useSavedViews } from '../lib/useSavedViews';

/**
 * SavedViewsControl — Pulsante "Vista" che apre un popover con:
 *  - Salva vista corrente (input nome + tasto Salva)
 *  - Lista delle viste salvate (click per applicare, X per rimuovere)
 *
 * Props:
 *  - scopeKey: string univoco (es. 'overview', 'area')
 *  - currentPayload: snapshot dei filtri correnti da memorizzare al "Salva"
 *  - onApply(payload): callback quando l'utente clicca su una vista salvata
 *  - testIdPrefix: prefisso per data-testid
 */
export function SavedViewsControl({ scopeKey, currentPayload, onApply, testIdPrefix = 'saved-views' }) {
  const { views, save, remove } = useSavedViews(scopeKey);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');

  const handleSave = () => {
    const v = save(name, currentPayload);
    if (v) {
      setName('');
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="rounded-none h-9 px-3 font-normal"
          data-testid={`${testIdPrefix}-trigger`}
        >
          <Bookmark className="w-4 h-4 mr-2 text-slate-500" />
          <span className="text-sm">Viste {views.length > 0 && <span className="text-slate-400">({views.length})</span>}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 rounded-none" align="end">
        <div className="p-3 border-b border-slate-100">
          <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">
            Salva vista corrente
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
              placeholder="Es. Top 5 negozi · Q1"
              className="flex-1 px-2 py-1.5 text-sm border border-slate-200 rounded-none focus:outline-none focus:border-slate-900"
              data-testid={`${testIdPrefix}-name-input`}
            />
            <Button
              size="sm"
              type="button"
              className="rounded-none h-8"
              disabled={!name.trim()}
              onClick={handleSave}
              data-testid={`${testIdPrefix}-save-btn`}
            >
              <Plus className="w-3 h-3 mr-1" /> Salva
            </Button>
          </div>
          <p className="text-[10px] text-slate-400 mt-2">
            Memorizza il periodo, l'anno e i filtri attivi.
          </p>
        </div>
        <div className="max-h-72 overflow-y-auto py-1" data-testid={`${testIdPrefix}-list`}>
          {views.length === 0 ? (
            <p className="px-3 py-4 text-xs text-slate-400 italic">
              Nessuna vista salvata. Configura i filtri e premi "Salva".
            </p>
          ) : (
            views.map((v) => (
              <div
                key={v.id}
                className="flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-slate-50 group border-b border-slate-50 last:border-b-0"
                data-testid={`${testIdPrefix}-item-${v.id}`}
              >
                <button
                  type="button"
                  className="flex-1 text-left min-w-0"
                  onClick={() => { onApply?.(v.payload); setOpen(false); }}
                  data-testid={`${testIdPrefix}-apply-${v.id}`}
                >
                  <span className="font-medium text-slate-900 block truncate">{v.name}</span>
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider">
                    {new Date(v.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: '2-digit' })}
                  </span>
                </button>
                <button
                  type="button"
                  className="opacity-50 hover:opacity-100 text-slate-500 hover:text-red-600 p-1"
                  onClick={() => remove(v.id)}
                  aria-label={`Rimuovi vista ${v.name}`}
                  data-testid={`${testIdPrefix}-remove-${v.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
